import gsap from "gsap";

export default function initGalleries() {
  document.querySelectorAll('.gallery').forEach((block) => {
    const root = block.querySelector('.slider');
    const track = block.querySelector('.slider__track');
    const slides = Array.from(track.querySelectorAll('.slide'));
    const prevBtn = root.querySelector('.btn--prev');
    const nextBtn = root.querySelector('.btn--next');


    const captionEl = root.querySelector('.slider__caption');

    // prefer inner element, else data-caption, else empty
    function getSlideCaption(i){
      const s = slides[i];
      if (s.dataset && s.dataset.caption) return s.dataset.caption.trim();
      return '';
    }

    // fade the caption when it changes (nice but quick)
    function updateCaption(){
      const text = getSlideCaption(index);
      gsap.to(captionEl, { autoAlpha: 0, duration: 0.12, onComplete(){
        captionEl.textContent = text || '';
        gsap.to(captionEl, { autoAlpha: 1, duration: 0.18 });
      }});
    }




    // CONFIG -------------
    const LOOP = false;
    const DUR  = 0.45;
    const EASE = "power3.out";
    const INACTIVE_OPACITY = 0.0001;

    let index = 0;
    let animating = false;

    // Prepare initial transforms for all slides
    function layout(){
      gsap.set(slides, { clearProps: "all" }); // reset if resized
      applyState(index, 0); // no animation
      updateButtons();
      updateCaption();
    }

    // Compute and apply transforms for current index and neighbors
    function applyState(i, duration = DUR, direction = 1){
      const tl = gsap.timeline({ defaults:{ duration, ease:EASE }, onStart(){ animating = true }, onComplete(){ animating = false } });
      const total = slides.length;

      // helper to map logical position to actual slide DOM index
      const idx = (k) => ( (k % total) + total ) % total;

      // z-index layering: active on top, then peek1, peek2, others bottom
      const zActive = 30, zPeek1 = 20, zPeek2 = 10, zRest = 1;
      const peekX1 = (window.innerWidth <= 991) ? 150 : 420;
      const peekY1 = (window.innerWidth <= 991) ? 0 : 50;

      slides.forEach((s, n) => {
        s.classList.remove('is-active');
        let role = 99; // default: hidden/parked

        if (n === i) {  // active
          role = 0; 
          s.classList.add('is-active');
        }

        if (LOOP) {
          if (n === (i + 1) % total) role = 1;       // next
          if (n === (i + 2) % total) role = 2;       // second next
        } else {
          if (n === i + 1 && n < total) role = 1;    // next (only if exists)
          if (n === i + 2 && n < total) role = 2;    // second next (only if exists)
        }

        if (role === 0){
          tl.to(s, { x:0, y:0, scale:1, rotation:0, opacity:1, zIndex:30, duration }, 0);
        } else if (role === 1){
          tl.to(s, { x: peekX1, y: peekY1, scale: 0.55, rotation:-6, opacity:1, zIndex:20, duration }, 0);
        } else if (role === 2){
          tl.to(s, { x: 490, y: 30, scale:0.5, rotation:8, opacity:1, zIndex:10, duration }, 0);
        } else {
          tl.to(s, { x:-200, y:0, scale:0, rotation:0, opacity:0.0001, zIndex:1, duration }, 0);
        }
      });

      return tl;
    }

    function next(){
      if (animating) return;
      if (!LOOP && index >= slides.length - 1) return;
      index = LOOP ? (index + 1) % slides.length : Math.min(index + 1, slides.length - 1);
      applyState(index, DUR, +1);
      updateButtons();
      updateCaption();
    }

    function prev(){
      if (animating) return;
      if (!LOOP && index <= 0) return;
      index = LOOP ? (index - 1 + slides.length) % slides.length : Math.max(index - 1, 0);
      applyState(index, DUR, -1);
      updateButtons();
      updateCaption();
    }

    function updateButtons(){
      if (LOOP){
        prevBtn.disabled = nextBtn.disabled = false;
        prevBtn.classList.remove('swiper-button-disabled');
        nextBtn.classList.remove('swiper-button-disabled');
        return;
      }
      const atStart = index === 0;
      const atEnd   = index === slides.length - 1;

      prevBtn.disabled = atStart;
      nextBtn.disabled = atEnd;

      prevBtn.classList.toggle('swiper-button-disabled', atStart);
      nextBtn.classList.toggle('swiper-button-disabled', atEnd);
    }

    // Controls
    nextBtn.addEventListener('click', next);
    prevBtn.addEventListener('click', prev);

    // Keyboard
    root.addEventListener('keydown', (e)=>{
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft')  prev();
    });
    root.tabIndex = 0; // focusable container

    // Drag on mobile only
    const isTouch = matchMedia('(pointer:coarse)').matches;
    if (isTouch){
      let startX = 0, deltaX = 0, dragging = false, t0 = 0;

      const onStart = (x) => { startX = x; deltaX = 0; dragging = true; t0 = performance.now(); };
      const onMove  = (x) => {
        if (!dragging || animating) return;
        deltaX = x - startX;
        const progress = gsap.utils.clamp(-1, 1, deltaX / 140); // small pull to preview
        // Nudge active + peeks for feedback
        const peekX = 100, gapY = 0;
        const a = slides[(index)%slides.length];
        const p1 = slides[(index+1)%slides.length];
        const p2 = slides[(index+2)%slides.length];

        gsap.set(a, { x: progress * -30 });
        gsap.set(p1, { x: peekX + progress * -40 });
        gsap.set(p2, { x: peekX * 1.15 + progress * -60 });
      };
      const onEnd   = () => {
        if (!dragging) return;
        dragging = false;
        const dt = performance.now() - t0;
        const v = Math.abs(deltaX) / Math.max(1, dt); // crude velocity
        const threshold = 60;
        if (deltaX <= -threshold || (v > 0.6 && deltaX < 0)) next();
        else if (deltaX >= threshold || (v > 0.6 && deltaX > 0)) prev();
        else applyState(index, 0.25); // snap back
      };

      track.addEventListener('touchstart', e => onStart(e.touches[0].clientX), {passive:true});
      track.addEventListener('touchmove',  e => onMove(e.touches[0].clientX),  {passive:true});
      track.addEventListener('touchend',   onEnd);

    }

    // Resize handling (debounced)
    let rAf;
    window.addEventListener('resize', () => {
      cancelAnimationFrame(rAf);
      rAf = requestAnimationFrame(() => layout());
    });

    // Init
    layout();

  });

}
